import { FC, useEffect, useState } from 'react';
import { Tr } from '@utils';
import { AssesmentQuestion, AssesmentQuestionType, ParkAreaAssesmentAnswer } from '@services';
import { FormStepLayout, FormStepQuestion } from '@components/portal/common';
import { Checkbox, FormControlLabel, Grid, MenuItem, Radio } from '@mui/material';
import { FormikTextfield } from '@components/common';

export interface IParkAreaAssementQuestionProps {
	questions: AssesmentQuestion[];
	answers: ParkAreaAssesmentAnswer[];
	index: number;
	onBack?: () => void;
	onNext?: (question: AssesmentQuestion, answer: string[], originalAnswer?: ParkAreaAssesmentAnswer) => void;
}

export const ParkAreaAssementQuestion: FC<IParkAreaAssementQuestionProps> = ({ questions, answers, index, onBack, onNext }) => {
	const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
	const [answer, setAnswer] = useState<ParkAreaAssesmentAnswer>();
	const [question, setQuestion] = useState<AssesmentQuestion>();

	useEffect(() => {
		const que = questions[index];
		const ans = answers?.find?.(({ assesmentQuestionId }) => assesmentQuestionId === que?.id);
		setQuestion(que);
		setAnswer(ans);
		setSelectedAnswers(ans && Array.isArray(ans.answer) ? ans.answer : []);
	}, [index, questions, answers]);

	return question ? (
		<FormStepLayout
			primaryButtonProps={{
				onClick: () => onNext?.(question, selectedAnswers, answer),
				'data-testid': 'add-edit-park-area-assesment-next-button',
				disabled: !selectedAnswers.length,
				children: index + 1 >= questions.length ? <Tr.Portal path="complete" /> : null
			}}
			secondaryButtonProps={{ onClick: onBack, 'data-testid': 'add-edit-park-area-assesment-back-button' }}
			hideSecondaryButton={index === 0}
		>
			<FormStepQuestion title={questions[index].questionText}>
				{question?.options?.length && (
					<Grid container spacing={4}>
						{question.questionType === AssesmentQuestionType.RADIO &&
							question.options.map((option, i) => (
								<Grid key={`question-${question.id}-answer-${option.answer}-${i}`} item xs={6}>
									<FormControlLabel
										label={option.answer}
										control={
											<Radio
												checked={selectedAnswers.includes(option.answer)}
												onChange={(_e, checked) => setSelectedAnswers(checked ? [option.answer] : [])}
											/>
										}
									/>
								</Grid>
							))}
						{question.questionType === AssesmentQuestionType.CHECKBOX &&
							question.options.map((option, i) => (
								<Grid key={`question-${question.id}-answer-${option.answer}-${i}`} item xs={6}>
									<FormControlLabel
										label={option.answer}
										control={
											<Checkbox
												checked={selectedAnswers.includes(option.answer)}
												onChange={(_e, checked) =>
													setSelectedAnswers(
														checked
															? [...new Set([...selectedAnswers, option.answer])]
															: selectedAnswers.filter((ans) => ans !== option.answer)
													)
												}
											/>
										}
									/>
								</Grid>
							))}
						{[AssesmentQuestionType.DROPDOWN, AssesmentQuestionType.MULTI_DROPDOWN].includes(question.questionType) && (
							<Grid item xs={6}>
								<FormikTextfield
									name=""
									select
									withoutFormik
									SelectProps={{ multiple: question.questionType === AssesmentQuestionType.MULTI_DROPDOWN }}
									value={selectedAnswers}
									onChange={(e) => {
										setSelectedAnswers(typeof e.target.value === 'string' ? [e.target.value] : e.target.value);
									}}
								>
									{question.options.map((option, i) => (
										<MenuItem key={`question-${question.id}-answer-${option.answer}-${i}`} value={option.answer}>
											{option.answer}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
						)}
					</Grid>
				)}
			</FormStepQuestion>
		</FormStepLayout>
	) : null;
};
